import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Loader2 } from 'lucide-react';

/**
 * Frontend route guard for protected pages.
 * Server-side data protection is enforced by entity RLS;
 * this component prevents rendering protected UI for unauthenticated
 * or wrong-user-type visitors before data calls are made.
 *
 * @param {React.ReactNode} children     - The page component to render
 * @param {string[]|null} allowedTypes   - Allowed user_types, or null for any authenticated user
 */
export default function ProtectedRoute({ children, allowedTypes = null }) {
  const [state, setState] = useState({ loading: true, user: null });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authenticated = await base44.auth.isAuthenticated();
        if (!authenticated) {
          base44.auth.redirectToLogin(window.location.href);
          setState({ loading: false, user: null });
          return;
        }
        const userData = await base44.auth.me();
        setState({ loading: false, user: userData });
      } catch {
        base44.auth.redirectToLogin(window.location.href);
        setState({ loading: false, user: null });
      }
    };
    checkAuth();
  }, []);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!state.user) {
    return null; // redirect handled in useEffect
  }

  if (allowedTypes && !allowedTypes.includes(state.user.user_type)) {
    const dashboard = state.user.user_type === 'facility'
      ? '/FacilityDashboard'
      : state.user.user_type === 'staffing_agency'
        ? '/StaffingAgencyDashboard'
        : state.user.user_type === 'worker'
          ? '/WorkerDashboard'
          : '/Onboarding';
    return <Navigate to={dashboard} replace />;
  }

  return children;
}